import { useFormikContext } from 'formik';
import uniqBy from 'lodash/uniqBy';
import { t } from 'Src/i18n';
import moment from 'moment';
import React from 'react';
import { constants } from '@clubmed/utils';
import { FormikInput as FormField } from 'Components/frameworks/formik/FormikInput';
import { Title } from 'Components/frameworks/title/Title';
type EventFormAffinityFieldsProps = {
  displayAddressFields: boolean;
  typeOptions: Array<{
    value: string;
    name: string;
  }>;
  regions: Array<{
    value: string;
    name: string;
  }>;
  resorts: Array<EventFormResort>;
};
const { OPENING_STATUS } = constants;
export const EventFormAffinityFields = ({
  displayAddressFields = true,
  typeOptions,
  regions,
  resorts,
}: EventFormAffinityFieldsProps) => {
  const { values } = useFormikContext();
  const openedResorts = resorts.filter(
    ({ openingStatus }) => openingStatus === OPENING_STATUS.OPEN,
  );
  return (
    <React.Fragment>
      <Title className="mb-10 mt-30 px-10" hLevel={3} theme="title-sans-n4">
        {t('eventForm.groupSection.title')}
      </Title>
      <div className="flex flex-wrap">
        <div className="w-full p-10 md:w-1/4">
          <FormField
            isRequired
            className="z-1"
            isDateRange
            label={t('eventForm.groupSection.datesLabel')}
            minDate={moment()
              .add({
                days: 1,
              })
              .toDate()}
            name="dates"
            placeholder={t('eventForm.groupSection.datesPlaceholder')}
            type="datepicker"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <FormField
            isRequired
            dataTestId="event-participants"
            label={t('eventForm.groupSection.numberOfParticipantsLabel')}
            min={1}
            name="numberOfParticipants"
            optimized
            placeholder={t('eventForm.groupSection.numberOfParticipantsPlaceholder')}
            type="number"
          />
        </div>
      </div>
      <div className="flex flex-wrap">
        <div className="w-full md:w-1/2">
          <div className="w-full p-10">
            <FormField
              dataTestId="event-region"
              label={t('eventForm.groupSection.destinationLabel')}
              name="region"
              options={regions}
              placeholder={t('eventForm.groupSection.destinationPlaceholder')}
              type="select"
            />
          </div>
          <div className="w-full p-10">
            <FormField
              dataTestId="event-resort"
              label={t('eventForm.groupSection.resortLabel')}
              name="resort"
              options={openedResorts
                .filter((resort) => !values.region || resort.regionId === values.region)
                .map((resort) => ({
                  name: resort.name,
                  value: resort.id,
                }))}
              placeholder={t('eventForm.groupSection.resortPlaceholder')}
              type="select"
            />
          </div>
        </div>
        <div className="w-full p-10">
          <FormField
            label={t('eventForm.groupSection.commentLabel')}
            maxLength={255}
            name="generalComment"
            placeholder={t('eventForm.groupSection.commentPlaceholder')}
            type="textarea"
          />
        </div>
      </div>
      <Title className="mb-10 mt-30 px-10" hLevel={3} theme="title-sans-n4">
        {t('eventForm.userSection.title')}
      </Title>
      <div className="flex flex-wrap">
        <div className="w-full p-10 md:w-1/2">
          <FormField
            dataTestId="event-type"
            isRequired
            label={t('eventForm.userSection.clientTypeLabel')}
            name="type"
            options={uniqBy(typeOptions, 'value')}
            placeholder={t('eventForm.userSection.clientTypePlaceholder')}
            type="select"
          />
        </div>
        <div className="flex w-full flex-wrap">
          <div className="w-full p-10 md:w-1/6">
            <FormField
              dataTestId="event-owner-company"
              label={t('eventForm.userSection.companyLabel')}
              maxLength={255}
              name="company"
              optimized
              placeholder={t('eventForm.userSection.companyPlaceholder')}
              type="text"
            />
          </div>
          <div className="w-full p-10 md:w-1/2">
            <FormField
              dataTestId="event-owner-lastName"
              isRequired
              label={t('eventForm.userSection.lastNameLabel')}
              maxLength={255}
              name="lastName"
              optimized
              placeholder={t('eventForm.userSection.lastNamePlaceholder')}
              type="text"
            />
          </div>
          <div className="w-full p-10 md:w-1/3">
            <FormField
              dataTestId="event-owner-firstName"
              isRequired
              label={t('eventForm.userSection.firstNameLabel')}
              maxLength={255}
              name="firstName"
              optimized
              placeholder={t('eventForm.userSection.firstNamePlaceholder')}
              type="text"
            />
          </div>
        </div>
        <div className="w-full p-10 md:w-1/2">
          <FormField
            dataTestId="event-owner-email"
            isRequired
            label={t('eventForm.userSection.emailLabel')}
            maxLength={255}
            name="email"
            optimized
            placeholder={t('eventForm.userSection.emailPlaceholder')}
            type="text"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <FormField
            dataTestId="event-owner-phone"
            label={t('eventForm.userSection.phoneLabel')}
            maxLength={255}
            name="phone"
            optimized
            placeholder={t('eventForm.userSection.phonePlaceholder')}
            type="text"
          />
        </div>
        {displayAddressFields && (
          <div className="flex w-full flex-wrap">
            <p className="mb-10 w-full px-10 pt-10 text-14 font-bold">
              {t('eventForm.userSection.addressLabel')}
            </p>
            <div className="w-full p-10 md:w-1/2">
              <FormField
                dataTestId="event-owner-address"
                label={t('eventForm.userSection.streetLabel')}
                maxLength={255}
                name="address"
                optimized
                placeholder={t('eventForm.userSection.streetPlaceholder')}
                type="text"
              />
            </div>
            <div className="w-1/2 p-10 md:w-1/6">
              <FormField
                dataTestId="event-owner-postcode"
                label={t('eventForm.userSection.postcodeLabel')}
                maxLength={255}
                name="postcode"
                optimized
                placeholder={t('eventForm.userSection.postcodePlaceholder')}
                type="text"
              />
            </div>
            <div className="w-full md:w-1/3">
              <div className="w-full p-10">
                <FormField
                  dataTestId="event-owner-city"
                  label={t('eventForm.userSection.cityLabel')}
                  maxLength={255}
                  name="city"
                  optimized
                  placeholder={t('eventForm.userSection.cityPlaceholder')}
                  type="text"
                />
              </div>
              <div className="w-full p-10">
                <FormField
                  dataTestId="event-owner-country"
                  label={t('eventForm.userSection.countryLabel')}
                  maxLength={255}
                  name="country"
                  optimized
                  placeholder={t('eventForm.userSection.countryPlaceholder')}
                  type="text"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap">
        <div className="w-full p-10">
          <p className="mb-5">{t('eventForm.userSection.optinLabel')}</p>
          <FormField
            className="mb-10"
            label={t('eventForm.userSection.emailOptinLabel')}
            name="optinEmail"
            optimized
            type="checkbox"
          />
          <FormField
            className="mb-10"
            label={t('eventForm.userSection.smsOptinLabel')}
            name="optinSMS"
            optimized
            type="checkbox"
          />
          <FormField
            className="mb-10"
            label={t('eventForm.userSection.phoneOptinLabel')}
            name="optinPhone"
            optimized
            type="checkbox"
          />
        </div>
      </div>
    </React.Fragment>
  );
};
